import { StatusChip, type ChipTone } from '@/components/sales/StatusChip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { inr } from '@/lib/sales-metrics';
import type {
  MigrationClientOutcome,
  MigrationClientResult,
  MigrationReport,
} from '@/services/migration.service';

import { MigrationIssueList } from './MigrationIssueList';

const OUTCOME_TONE: Record<MigrationClientOutcome, ChipTone> = {
  import: 'success',
  existing: 'info',
  skip: 'destructive',
};

interface MigrationClientTableProps {
  clients: MigrationClientResult[];
  mode: MigrationReport['mode'];
  emptyText: string;
}

const outcomeLabel = (outcome: MigrationClientOutcome, mode: MigrationReport['mode']): string => {
  if (outcome === 'existing') return 'Already in CRM';
  if (outcome === 'skip') return 'Skipped';
  return mode === 'commit' ? 'Imported' : 'Ready';
};

export const MigrationClientTable = ({ clients, mode, emptyText }: MigrationClientTableProps) => {
  if (clients.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">{emptyText}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Client Code</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>ARN</TableHead>
            <TableHead>RM</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Renewals (next due · per cycle)</TableHead>
            <TableHead className="min-w-[340px]">Reasons / Notes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clients.map((c) => (
            <TableRow key={`${c.clientCode}-${c.sheetRows.join('-')}`} className="align-top">
              <TableCell className="font-mono text-xs">
                <p className="font-semibold">{c.clientCode}</p>
                <p className="text-muted-foreground">{`Row ${c.sheetRows.join(', ')}`}</p>
                {c.crmClientCode != null && (
                  <p className="text-primary">{`CRM #${c.crmClientCode}`}</p>
                )}
              </TableCell>
              <TableCell className="font-semibold">{c.clientName}</TableCell>
              <TableCell className="font-mono text-xs">{c.arn || '-'}</TableCell>
              <TableCell className="text-xs">{c.rmCode || '-'}</TableCell>
              <TableCell>
                <StatusChip tone={OUTCOME_TONE[c.outcome]}>{outcomeLabel(c.outcome, mode)}</StatusChip>
              </TableCell>
              <TableCell className="text-xs">
                <ul className="space-y-0.5">
                  {c.subscriptions.map((s) => (
                    <li key={s.sheetRow} className="whitespace-nowrap">
                      <span className="font-semibold">
                        {s.subItemCode ? `${s.productCode} / ${s.subItemCode}` : s.productCode}
                      </span>
                      {` · ${s.nextDueDate ?? '?'}`}
                      {s.perCycleAmount != null && ` · ${inr(s.perCycleAmount)}`}
                    </li>
                  ))}
                </ul>
              </TableCell>
              <TableCell>
                <MigrationIssueList issues={c.issues} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
